import { useContext, useState } from 'react'
import { useParams } from 'react-router'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Button, DialogContent, FormControlLabel, Grid, IconButton, Radio, RadioGroup, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import { AppContext } from '../../providers/app-provider'
import { useAuthentication } from '../../providers/auth-provider'
import PeopleContext from '../../routes/people/context'
import Api from '../../utils/api'
import DialogWithTitle from '../dialog-with-title'
import FormikDropdown from '../formik-dropdown'
import FormikPreferencesToggleButton from '../formik-preferences-toggle-button'
import FormikTextField from '../formik-textfield'

import useStyles from './index.styles'

type AppsType = 'protect' | 'insights' | 'central'

let alreadyValidatedMobileNumber: string
let currentlyValidatingMobileNumber: string | undefined
let isEnteringMobileNumber = false

let alreadyValidatedBackupNumber: string
let currentlyValidatingBackupNumber: string | undefined
let isEnteringBackupNumber = false

interface FormValues {
  roleId: string
  first: string
  last: string
  email: string
  currPassword: string
  phone: string
  phone_ext: string
  backup_phone: string
  backup_phone_ext: string
  notification: string[]
  invitation_app: AppsType | undefined
}

interface IProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  mode: 'create' | 'edit'
  setPeopleTabRefreshCounter?: React.Dispatch<React.SetStateAction<number>>
  user: IUserType | undefined // user to show in form in edit mode
  setUser?: React.Dispatch<React.SetStateAction<IUserType | undefined>> // if need to update user when PeopleContext exists
}

const UserModal = (props: IProps) => {
  const { open, setOpen, mode, setPeopleTabRefreshCounter, user, setUser } = props

  const classes = useStyles

  const { auth, ownUser, hasInternalUserAccess } = useAuthentication()
  const { membershipStatus, setMembershipStatus } = useContext(PeopleContext)
  const { roles, host, refreshPage } = useContext(AppContext)

  const { enqueueSnackbar } = useSnackbar()
  const routingParams = useParams<{ accountId: string }>()

  const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false)

  const rolesDropdown = roles?.map((role) => ({ id: String(role.id), name: role.name })) ?? []
  const callCenterRoleId = roles?.find((role) => role.name === 'Call Center')?.id

  const initialValues = {
    roleId: String(user?.roleId ?? ''),
    first: user?.information?.first ?? '',
    last: user?.information?.last ?? '',
    email: user?.email ?? '',
    currPassword: '',
    phone: user?.phone ?? '',
    phone_ext: (user?.metadata?.phone_ext as string | undefined) ?? '',
    backup_phone: user?.metadata?.backup_phone ?? '',
    backup_phone_ext: (user?.metadata?.backup_phone_ext as string | undefined) ?? '',
    notification: user?.preferences?.notification ?? [],
    invitation_app: undefined,
  }

  const schema = Yup.object().shape({
    roleId: Yup.string().required('Required'),
    first: Yup.string().required('Required'),
    last: Yup.string().test('requiredLastName', 'Required', function testLastName() {
      return this.parent.roleId === String(callCenterRoleId) || !!this.parent.last?.trim() // Required for notification contacts, but not call centers
    }),
    email: Yup.string().trim().required('Required').email('Please enter correct email format.'),
    currPassword: Yup.string().test(
      'requiredIfChangingEmail',
      "Your current password is required if changing your account's email address.",
      function testCurrentPassword() {
        return (
          mode === 'create' ||
          user?.id !== ownUser?.id ||
          (user?.id === ownUser?.id && this.parent.email === user?.email) ||
          (user?.id === ownUser?.id && this.parent.email !== user?.email && this.parent.currPassword)
        )
      }
    ),
    phone: Yup.string()
      .required('Required')
      .test('validMobileNumber', 'Please enter a valid phone number.', function testMobileNumber() {
        const phoneNumber = (this.parent.phone as string).replace(/[^+\d]/g, '') // Remove everything except digits and optional + to avoid the endpoint's max character limit of 15

        // To limit the number of API calls, we only check that this is a valid mobile number via the API if it hasn't already been checked and if the user has left the field
        if (alreadyValidatedMobileNumber !== phoneNumber && currentlyValidatingMobileNumber !== phoneNumber && !isEnteringMobileNumber) {
          currentlyValidatingMobileNumber = phoneNumber
          return new Promise((resolve) => {
            if (auth.user?.access_token)
              Api.get(`${host}/api/users/phoneinfo?number=${phoneNumber}`)
                .then((res) => {
                  if (
                    res.ok &&
                    ((this.parent.roleId !== String(callCenterRoleId) && !res.body.carrier.error_code && res.body.carrier.type === 'mobile') || // Mobile number for most roles
                      (this.parent.roleId === String(callCenterRoleId) && !res.body.carrier.error_code)) // Any phone type for Call Centers
                  ) {
                    alreadyValidatedMobileNumber = phoneNumber
                    resolve(true)
                  } else {
                    resolve(false)
                  }
                })
                .finally(() => {
                  currentlyValidatingMobileNumber = undefined
                })
          })
        }
        return true
      }),
    backup_phone: Yup.string().test('validBackupNumber', 'Please enter a valid phone number.', function testBackupNumber() {
      if (!this.parent.backup_phone) return true

      const phoneNumber = (this.parent.backup_phone as string).replace(/-|\s/g, '') // Remove spaces and hyphens to avoid the endpoint's max character limit of 15

      // To limit the number of API calls, we only check that this is a valid phone number via the API if it hasn't already been checked and if the user has left the field
      if (alreadyValidatedBackupNumber !== phoneNumber && currentlyValidatingBackupNumber !== phoneNumber && !isEnteringBackupNumber) {
        currentlyValidatingBackupNumber = phoneNumber
        return new Promise((resolve) => {
          if (auth.user?.access_token)
            Api.get(`${host}/api/users/phoneinfo?number=${phoneNumber}`)
              .then((res) => {
                if (res.ok && !res.body.carrier.error_code) {
                  alreadyValidatedBackupNumber = phoneNumber
                  resolve(true)
                } else {
                  resolve(false)
                }
              })
              .finally(() => {
                currentlyValidatingBackupNumber = undefined
              })
        })
      }
      return true
    }),
    backup_phone_ext: Yup.string().test(
      'requiredBackupPhoneForExtension',
      'Backup number is required if entering an extension.',
      function testBackupPhoneExt() {
        return !this.parent.backup_phone_ext || (this.parent.backup_phone_ext && this.parent.backup_phone)
      }
    ),
  })

  const isCallCenter = (values: FormValues) => values.roleId === String(callCenterRoleId)

  const createMembership = async (userId: string, externalApp: string) => {
    if (!auth.user?.access_token) return
    const params = { referToApplication: 'central', userId, externalApp }
    const urlParams = new URLSearchParams(params)
    const res = await Api.post(`${host}/api/authentication/membership?${urlParams.toString()}`, {})
    if (res.ok) {
      setMembershipStatus('Active')
      enqueueSnackbar(`Invitation email for ${externalApp} was sent.`)
    } else {
      enqueueSnackbar(`Could not send an invite email for ${externalApp}.`, { variant: 'error' })
    }
  }

  const onSubmit = async (values: FormValues) => {
    if (!auth.user?.access_token) return
    if (user && mode === 'edit') {
      const payload = {
        ...user,
        roleId: Number(values.roleId),
        email: values.email !== user.email ? values.email : undefined,
        currPassword: values.currPassword,
        phone: values.phone,
        information: {
          ...user.information,
          first: values.first,
          last: values.last,
        },
        metadata: {
          ...user.metadata,
          phone_ext: values.phone_ext,
          phone_type: isCallCenter(values) ? 'Landline' : 'Mobile Phone',
          backup_phone: values.backup_phone,
          backup_phone_ext: values.backup_phone_ext,
          backup_phone_type: user.metadata?.backup_phone_type ?? 'Landline',
        },
        preferences: {
          ...user.preferences,
          notification: values.notification,
        },
      }

      const res = await Api.put(`${host}/api/users/${user.id}`, payload)

      if (res.ok) {
        setUser?.(res.body)
        enqueueSnackbar('User updated successfully.')
        setPeopleTabRefreshCounter?.((c) => c + 1)
        if (values.invitation_app) await createMembership(String(user.id), values.invitation_app)
        setOpen(false)
      } else {
        enqueueSnackbar('Could not update user.', { variant: 'error' })
      }
    } else if (mode === 'create') {
      const payload = {
        roleId: Number(values.roleId),
        email: values.email,
        phone: values.phone,
        folders: [Number(routingParams.accountId)],
        information: {
          first: values.first,
          last: values.last,
        },
        metadata: {
          phone_ext: values.phone_ext,
          phone_type: isCallCenter(values) ? 'Landline' : 'Mobile Phone',
          backup_phone: values.backup_phone,
          backup_phone_ext: values.backup_phone_ext,
          backup_phone_type: 'Landline',
        },
        preferences: {
          notification: values.notification,
        },
      }

      const res = await Api.post(`${host}/api/users`, payload)

      if (res.ok) {
        enqueueSnackbar('User created successfully.')
        setOpen(false)
        refreshPage()
        if (values.invitation_app) await createMembership(String(res.body.id), values.invitation_app)
      } else {
        enqueueSnackbar('Could not create user.', { variant: 'error' })
      }
    }
  }

  return (
    <DialogWithTitle
      title={mode === 'create' ? 'Add' : 'Edit'}
      open={open}
      setOpen={setOpen}
      data-cy={mode === 'create' ? 'addNewContact' : 'editContact'}
      fullWidth={true}
      maxWidth="sm"
    >
      <DialogContent>
        <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize={true} validationSchema={schema}>
          {({ values, handleChange, handleBlur, isValid, isSubmitting }) => (
            <Form>
              <Typography variant="subtitle2">Role</Typography>

              <Grid size={12}>
                <FormikDropdown
                  name="roleId"
                  label="Role"
                  required={true}
                  items={
                    hasInternalUserAccess
                      ? rolesDropdown.sort((a, b) => a.name.localeCompare(b.name))
                      : rolesDropdown
                          .filter((role) => role.name === 'Notification Contact' || role.name === 'Location Manager')
                          .sort((a, b) => a.name.localeCompare(b.name))
                  }
                  size="medium"
                  data-cy="roleSelection"
                />
              </Grid>

              <Typography
                variant="subtitle2"
                sx={{
                  marginTop: 1,
                }}
              >
                Contact Information
              </Typography>

              <Grid container={true} sx={{ flexDirection: 'column' }} spacing="3px" size={12}>
                <Grid container={true} columnSpacing={2} size={12}>
                  <Grid size={isCallCenter(values) ? 12 : 6}>
                    <FormikTextField
                      label={isCallCenter(values) ? 'Name' : 'First Name'}
                      name="first"
                      required={true}
                      data-cy="firstName"
                      className="fs-exclude"
                    />
                  </Grid>

                  {!isCallCenter(values) && (
                    <Grid size={6}>
                      <FormikTextField label="Last Name" name="last" required={true} data-cy="lastName" className="fs-exclude" />
                    </Grid>
                  )}
                </Grid>

                <Grid size={12}>
                  <FormikTextField label="Email address" name="email" required={true} data-cy="email" className="fs-exclude" />
                </Grid>

                {values.email !== user?.email && user?.id === ownUser?.id && (
                  <Grid>
                    <FormikTextField
                      label="Current password"
                      name="currPassword"
                      className="fs-exclude"
                      required={true}
                      type={showCurrentPassword ? 'text' : 'password'}
                      endAdornment={
                        <IconButton aria-label="toggle password visibility" onClick={() => setShowCurrentPassword(!showCurrentPassword)} size="small">
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      }
                    />
                  </Grid>
                )}

                <Grid container={true} direction="row" columnSpacing={2}>
                  <Grid container={true} size={6}>
                    <FormikTextField
                      label={isCallCenter(values) ? 'Phone Number' : 'Mobile Number'}
                      name="phone"
                      required={true}
                      className="fs-exclude"
                      onChange={() => {
                        isEnteringMobileNumber = true
                      }}
                      onBlur={() => {
                        isEnteringMobileNumber = false
                      }}
                      data-cy="phoneNumber"
                    />
                  </Grid>
                  <Grid size={2}>{isCallCenter(values) && <FormikTextField label="Ext" name="phone_ext" />}</Grid>
                  <Grid size={4} />
                </Grid>

                <Grid container={true} direction="row" columnSpacing={2}>
                  <Grid container={true} size={6}>
                    <FormikTextField
                      label="Backup Number"
                      name="backup_phone"
                      className="fs-exclude"
                      onChange={() => {
                        isEnteringBackupNumber = true
                      }}
                      onBlur={() => {
                        isEnteringBackupNumber = false
                      }}
                      data-cy="backupPhone"
                    />
                  </Grid>
                  <Grid size={2}>
                    <FormikTextField label="Ext" name="backup_phone_ext" />
                  </Grid>
                  <Grid size={4} />
                </Grid>

                <Typography
                  variant="subtitle2"
                  sx={{
                    marginTop: 1,
                    marginBottom: 1,
                  }}
                >
                  Notification Preference
                </Typography>

                <FormikPreferencesToggleButton
                  name="notification"
                  options={[
                    { value: 'sms', label: 'Text' },
                    { value: 'phone', label: 'Phone' },
                    { value: 'email', label: 'Email' },
                    { value: 'push', label: 'Push', disabled: true, disabledTooltip: 'Selection by user, in app only' },
                  ]}
                />
              </Grid>

              {(membershipStatus === 'Deactivated' || mode === 'create') && (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      marginTop: 2,
                    }}
                  >
                    Send invitation email
                  </Typography>

                  <RadioGroup name="invitation_app" sx={classes.radioGroup}>
                    <Grid
                      container={true}
                      data-cy="app"
                      size={12}
                      sx={{
                        gap: 2,
                      }}
                    >
                      <FormControlLabel control={<Radio />} onChange={handleChange} onBlur={handleBlur} value="protect" label="Protect" />

                      {hasInternalUserAccess && (
                        <>
                          <FormControlLabel control={<Radio />} onChange={handleChange} onBlur={handleBlur} value="insights" label="Insights" />
                          <FormControlLabel control={<Radio />} onChange={handleChange} onBlur={handleBlur} value="central" label="Central" />
                        </>
                      )}
                    </Grid>
                  </RadioGroup>
                </>
              )}

              <Grid
                container={true}
                size={12}
                sx={{
                  justifyContent: 'flex-end',
                  gap: 2,
                  marginTop: 2,
                }}
              >
                <Button variant="outlined" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={!isValid || isSubmitting || isEnteringMobileNumber || isEnteringBackupNumber}>
                  Save
                </Button>
              </Grid>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </DialogWithTitle>
  )
}

export default UserModal
